import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Autocomplete, Chip } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import AsyncLoader from '~components/AsyncLoader';
import { DotLoader } from '~components/DotLoader';
import EmptyState from '~components/EmptyState';
import useDebounce from '~hooks/useDebounce';
import { updateCampaignGeneralDetailsById } from '~pages/CampaignManagement/api';
import useCampaignSearch from '~pages/CampaignManagement/CampaignList/useCampaignSearch';
import { useAuth } from '~providers/AuthProvider';
import { AccessScope } from '~providers/AuthProvider/domain';
import { useNotification } from '~providers/NotificationProvider';
import { useSetPageTitleProps } from '~providers/PageTitleProvider';
import Routes from '~providers/RouteProvider/Routes';
import { useUserPreferences } from '~providers/UserPreferencesProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';
import { pluraliseWord } from '~utils/Functions';

import { CreateCampaign } from '../domain';
import CampaignCard from './CampaignCard';
import CreateCampaignModal from './CreateCampaignModal';
import { CampaignFilterType } from './useCampaignSearch/domain';

type FilterOption = { value: CampaignFilterType; label: string };
const filterOptions: FilterOption[] = [
  { value: CampaignFilterType.Active, label: 'Active' },
  { value: CampaignFilterType.Paused, label: 'Paused' },
  { value: CampaignFilterType.Archived, label: 'Archived' },
];

const CampaignList = () => {
  const { hasScope } = useAuth();
  const { accessFilter } = useUserPreferences();
  const { pushNotification } = useNotification();
  const setPageTitleProps = useSetPageTitleProps();
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [submittingData, setSubmittingData] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>(
    filterOptions.filter((i) => i.value !== CampaignFilterType.Archived),
  );
  const filters = selectedFilters.map((i) => i.value);
  const debouncedSearch = useDebounce(search, 500);
  const {
    loading,
    error,
    list,
    hasMore,
    create,
    intersectionObserverRef: lastDataElement,
  } = useCampaignSearch(debouncedSearch, {
    accessFilterId: accessFilter?.id,
    filters,
  });

  const noSearchOrFilterSet = search === '' && filters.length === 0;

  // Set page title
  useEffect(() => {
    setPageTitleProps({ pageName: 'Campaigns' });
  }, []);

  const toggleCreateCampaignModal = () => {
    setCreateModalOpen((prev) => !prev);
  };

  const addCampaign = async (data: CreateCampaign) => {
    setSubmittingData(true);

    try {
      await create(data);
      setSubmittingData(false);
    } catch (e) {
      setSubmittingData(false);
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);
      return Promise.reject();
    }

    pushNotification('success', `Created campaign ${data.name}`);
    setCreateModalOpen(false);
  };

  const displayList = useMemo(
    () =>
      list.map((item, index) => {
        const isArchived = Boolean(item.archived);
        return (
          <CampaignCard
            key={item.id}
            ref={index === list.length - 1 ? lastDataElement : null}
            name={item.name}
            active={item.isActive}
            archived={isArchived}
            to={generatePath(Routes.viewCampaign.path, { campaignId: item.id.toString() })}
            toggleAction={
              hasScope(AccessScope.CanUpdateCampaignGeneralSettings) && !isArchived
                ? async (v: boolean) => {
                    await updateCampaignGeneralDetailsById(item.id, {
                      isActive: v,
                    });
                  }
                : undefined
            }
            toggleCallback={(v: boolean) => (item.isActive = v)}
            statisticItems={[
              { icon: ListAltIcon, text: `${item.numLists.toLocaleString()} ${pluraliseWord(item.numLists, 'list')}` },
            ]}
          />
        );
      }),

    [list, lastDataElement],
  );
  return (
    <>
      <Grid sx={{ marginBottom: 1 }} container spacing={1} alignContent='center'>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            variant='outlined'
            label='Search'
            id='search'
            name='search'
            defaultValue={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            fullWidth
            filterSelectedOptions
            onChange={(_, filters) => {
              setSelectedFilters(filters);
            }}
            value={selectedFilters}
            options={filterOptions}
            getOptionLabel={(option) => option.label}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  deleteIcon={<CancelIcon />}
                  label={option.label}
                  variant='filled'
                  color='primary'
                />
              ))
            }
            renderInput={(params) => <TextField {...params} label='Filters' variant='outlined' name='filters' />}
          />
        </Grid>

        <Hidden smDown>
          <Grid item md={2}></Grid>
        </Hidden>

        {hasScope(AccessScope.CanCreateCampaign) && (
          <Grid style={{ display: 'flex', alignItems: 'center' }} item xs={12} md={3}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              fullWidth
              startIcon={<AddIcon />}
              onClick={toggleCreateCampaignModal}>
              Create Campaign
            </Button>
          </Grid>
        )}
      </Grid>

      <AsyncLoader isLoading={loading && list.length === 0}>
        <Grid container spacing={1} alignContent='center'>
          <Grid item xs={12}>
            {list.length > 0 && (
              <>
                <List>{displayList}</List>
                {loading && list.length > 0 && <DotLoader align='center' />}

                {!loading && !hasMore && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    No more results to display
                  </Typography>
                )}

                {error && list.length > 0 && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    Failed to load campaigns
                  </Typography>
                )}
              </>
            )}

            {list.length === 0 && !noSearchOrFilterSet && (
              <EmptyState
                type='no-records-found'
                text='No campaigns found matching your search criteria'
                subText='Try alternate words or selections.'
              />
            )}

            {list.length === 0 && noSearchOrFilterSet && !accessFilter && (
              <EmptyState type='no-items-3' text='No campaigns currently exist' />
            )}

            {list.length === 0 && noSearchOrFilterSet && accessFilter && (
              <EmptyState
                type='no-records-found'
                text={`${accessFilter.name} access filter does not have any campaigns assigned to it`}
              />
            )}
          </Grid>
        </Grid>

        <CreateCampaignModal
          open={createModalOpen}
          submitting={submittingData}
          onClose={toggleCreateCampaignModal}
          onAccept={addCampaign}
        />
      </AsyncLoader>
    </>
  );
};

export default CampaignList;
